import axios from 'axios';

import { API_BASE, BACKEND_URL } from './apiBase';
const API_BASE_URL = API_BASE;

// Service pour gérer les paramètres système
class SettingsService {
  // Récupérer les paramètres système
  async getSettings() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return this.getPublicSettings();
      const response = await axios.get(`${API_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres:', error);
      
      throw error;
    }
  }

  // Toujours relire l'entreprise authentifiée avant d'émettre une facture.
  async getInvoiceSettings(invoice = {}) {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Veuillez vous reconnecter avant de générer la facture.');
    const response = await this.getSettings();
    const settings = response.settings;
    if (localStorage.getItem('token') !== token) {
      throw new Error('La session a changé. Veuillez relancer la génération de la facture.');
    }
    if (!response.success || !settings?.entreprise_id) {
      throw new Error('Les informations de votre entreprise sont indisponibles.');
    }
    if (invoice.entreprise_id != null && String(invoice.entreprise_id) !== String(settings.entreprise_id)) {
      throw new Error('Cette facture appartient à une autre entreprise.');
    }
    return settings;
  }

  // 🔴 NOUVEAU : Récupérer uniquement les paramètres publics
  async getPublicSettings() {
    try {
      const response = await axios.get(`${API_BASE_URL}/settings/public`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres publics:', error);
      throw error;
    }
  }

  // Mettre à jour les paramètres système
  async updateSettings(settings) {
    try {
      const response = await axios.put(`${API_BASE_URL}/settings`, settings);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      throw error;
    }
  }

  // Télécharger un logo
  async uploadLogo(logoFile) {
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await axios.post(`${API_BASE_URL}/settings/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      throw error;
    }
  }

  // Réinitialiser les paramètres
  async resetSettings() {
    try {
      const response = await axios.post(`${API_BASE_URL}/settings/reset`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation des paramètres:', error);
      throw error;
    }
  }

  // Valider les paramètres avant soumission
  validateSettings(settings) {
    const errors = [];

    if (!settings.nom || settings.nom.trim() === '') {
      errors.push('Le nom de l\'application est obligatoire');
    }

    if (!settings.version || settings.version.trim() === '') {
      errors.push('La version est obligatoire');
    }

    if (!settings.description || settings.description.trim() === '') {
      errors.push('La description est obligatoire');
    }

    if (settings.email && !this.validateEmail(settings.email)) {
      errors.push('L\'email n\'est pas valide');
    }

    return errors;
  }

  // Valider le format d'email
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Valider le fichier logo
  validateLogoFile(file) {
    const errors = [];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!file) {
      errors.push('Aucun fichier sélectionné');
      return errors;
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push('Format de fichier non autorisé. Seuls les fichiers JPEG, PNG, GIF et SVG sont acceptés.');
    }

    if (file.size > maxSize) {
      errors.push('Le fichier est trop volumineux. Taille maximale: 2MB');
    }

    return errors;
  }

  // Obtenir l'URL complète pour une image
  getImageUrl(imagePath) {
    if (!imagePath) return '/assets/logo.png';
    
    // Si c'est déjà une URL complète, la retourner
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Si c'est un chemin local, construire l'URL complète
    if (imagePath.startsWith('/uploads/')) {
      // Les fichiers sont servis par nginx sur /uploads en production et par le backend en développement.
      // L'ancienne URL codée sur localhost empêchait le logo de s'afficher hors du poste local.
      return `${BACKEND_URL}${imagePath}`;
    }
    
    // Pour les assets locaux
    return imagePath;
  }

  // URL du logo dynamique servi par le backend
  getLogoUrl(forceRefresh = false, logoPath = null) {
    // Utiliser API_BASE qui est déjà configuré correctement (/api ou http://localhost:55000/api)
    // Mais attention, API_BASE contient déjà /api, donc on ne doit pas le rajouter si on utilise /logo
    // Le backend sert le logo sur /api/logo
    const logo = logoPath || this.loadSettingsLocally()?.logo || '/assets/logo.png';
    const url = this.getImageUrl(logo);
    return forceRefresh && url.includes('?') ? `${url}&v=${Date.now()}` : (forceRefresh ? `${url}?v=${Date.now()}` : url);
  }

  // 🔴 NOUVEAU : Forcer le rechargement des logos
  clearLogoCache() {
    // Trouver toutes les images de logo et les recharger
    const logoImages = document.querySelectorAll('img[src*="logo"], img[alt*="Logo"], img[alt*="logo"]');
    logoImages.forEach(img => {
      const originalSrc = img.src.split('?')[0]; // Retirer les paramètres existants
      img.src = originalSrc + '?v=' + Date.now();
    });
  }

  // Sauvegarder les paramètres localement (fallback)
  saveSettingsLocally(settings) {
    try {
      const entrepriseId = localStorage.getItem('entrepriseId');
      if (!entrepriseId) return false;
      localStorage.setItem(`app-settings-${entrepriseId}`, JSON.stringify(settings));
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde locale:', error);
      return false;
    }
  }

  // Charger les paramètres locaux (fallback)
  loadSettingsLocally() {
    try {
      const entrepriseId = localStorage.getItem('entrepriseId');
      if (!entrepriseId) return null;
      const savedSettings = localStorage.getItem(`app-settings-${entrepriseId}`);
      if (savedSettings) {
        return JSON.parse(savedSettings);
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du chargement local:', error);
      return null;
    }
  }

  // Nettoyer les paramètres avant sauvegarde
  cleanSettings(settings) {
    return {
      nom: settings.nom ? settings.nom.trim() : '',
      version: settings.version ? settings.version.trim() : '',
      description: settings.description ? settings.description.trim() : '',
      email: settings.email ? settings.email.trim() : '',
      telephone: settings.telephone ? settings.telephone.trim() : '',
      adresse: settings.adresse ? settings.adresse.trim() : '',
      logo: settings.logo || '/assets/logo.png',
      cachet: settings.cachet || ''
    };
  }
}

export default new SettingsService();
